/**
 * propertyColumns —— 合作内容「属性列」的全部实现（单元格控件 / 单元格组件 / 列构建）。
 *
 * 【命名用 property 不用 attribute】仓库领域词汇是 property：
 * `property_values` / `propertyFieldConfig` / `propertyPipe` / `PropField` / `updateProperty` /
 * `getPropertyCode`。`getAttrColumnWidth` 是这条线上少数派用词，保留原名以免扩大改名面。
 *
 * 【为何属性列是 custom 列而非 doc 列】
 * 属性值存放在 `row.property_values[code]`（嵌套），引擎的 doc 列按 `row[field]` 取标量、取不到；
 * 且属性列的选项集**依赖整行上下文**（级联父字段变化 → 子字段合法选项集变化），
 * 引擎的 doc 列没有 per-row 求值的入口。故走 custom 列 + 自持渲染与校验判据。
 *
 * 【跨模式同源】这里的所有判据（必填 / 选项匹配 / 控件类型 / 列宽）都镜像折叠面板
 * `CooperationPropertyForm` + `#utils.getOptionsRule`。改这里前必须两侧一起看。
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Select, Input, InputNumber, DatePicker, Tooltip } from '@bedrock/components';
import { Pushpin } from '@bedrock/icons-react';
import dayjs from 'dayjs';
import { ComposingSafeInput, CELL_SELECT_DROPDOWN_PROPS, INPUT_VALIDATE_DEBOUNCE_MS, type ColumnMeta } from '@mpc/common/src/components/ProTableListForm';
import { DEFAULT_DATE_FORMAT } from '#utils/date';
import TextAreaPopover from '#components/SForm/SFormElement/TextAreaPropover';
import { getSchemaItemFieldConfig, isSchemaItemRequired, isCascadingAttr, type PropertyFieldConfig } from './propertyFieldConfig';
import { getPropertyCode, cooperationValueToFormValue } from './propertyPipe';
import { isEmptyFieldValue, getPropRequired, isPropValueMatched, validatePropField } from './propertyValidation';

/** 属性列宽：span:2（如 text/date_range）→ 宽列；其余（默认/span:1）→ 窄列。与 propertyFieldConfig 的 span 对齐。 */
const WIDE_ATTR_COLUMN_WIDTH = 300;
const NARROW_ATTR_COLUMN_WIDTH = 150;
/**
 * 单一日期字段（date_input）的 span 为 1，但单元格内是「日期文本 + 日历图标 + 清除按钮」的
 * DatePicker，窄列会被裁切；故与日期区间（date_range，span:2）取同一宽度，
 * 保证「日期」与「日期区间」两列视觉一致。
 */
const getAttrColumnWidth = (schemaItem: any): number => {
  const { span, controlType } = getSchemaItemFieldConfig(schemaItem);
  const isWide = span === 2 || controlType === 'date';
  return isWide ? WIDE_ATTR_COLUMN_WIDTH : NARROW_ATTR_COLUMN_WIDTH;
};

/** controlType='date'/'dateRange' 的值 <-> dayjs 归一化，逻辑对齐 SFormElement 的 datePicker 分支 */
const toDayjsValue = (value: any, isRange: boolean) => {
  const values = Array.isArray(value) ? value : [value];
  const finalValues = values.map((v) => (v ? dayjs(v) : v));
  return isRange ? finalValues : finalValues[0];
};
const fromDayjsValue = (changeValue: any, isRange: boolean) => {
  const changeValues = Array.isArray(changeValue) ? changeValue : [changeValue];
  const finalValues = changeValues.map((v) => (v?.isValid?.() ? v.format(DEFAULT_DATE_FORMAT) : v));
  return isRange ? finalValues : finalValues[0];
};

/** 按 controlType 分派渲染控件（select/input/numeric/date/dateRange/text[TextAreaPopover]） */
const renderPropertyControl = (
  fieldConfig: PropertyFieldConfig,
  value: any,
  onChange: (v: any) => void,
) => {
  const { controlType, elementProps = {} } = fieldConfig;
  switch (controlType) {
    case 'select': {
      const isMultiple = !!elementProps.multiple;
      const selectValue = isMultiple ? (Array.isArray(value) ? value : value !== undefined ? [value] : []) : value;
      return (
        <Select
          size="small"
          value={selectValue}
          options={elementProps.options}
          multiple={isMultiple}
          placeholder="请选择"
          allowClear
          style={{ width: '100%' }}
          {...CELL_SELECT_DROPDOWN_PROPS}
          onChange={(v: any) => onChange(v)}
        />
      );
    }
    case 'numeric':
      return (
        <InputNumber
          size="small"
          direction="vertical"
          value={value}
          toFixed={elementProps.toFixed}
          placeholder={elementProps.placeholder}
          style={{ width: '100%' }}
          onChange={(v: any) => onChange(v)}
        />
      );
    case 'date':
    case 'dateRange': {
      const isRange = controlType === 'dateRange';
      return (
        <DatePicker
          size="small"
          range={isRange}
          value={toDayjsValue(value, isRange)}
          placeholder={elementProps.placeholder}
          style={{ width: '100%' }}
          onChange={(v: any) => onChange(fromDayjsValue(v, isRange))}
        />
      );
    }
    case 'text':
      return (
        <TextAreaPopover
          value={value ?? ''}
          showConfirmButton
          textAreaProps={{ maxLength: elementProps.maxLength }}
          renderChildren={(v: string) => (
            <Input size="small" value={v} placeholder={elementProps.placeholder || '请输入'} style={{ width: '100%' }} readOnly />
          )}
          onChange={(v: string) => onChange(v)}
        />
      );
    case 'upload':
      // 【Demo TODO】text_with_upload 表格模式暂不支持，正式接入前先按只读文案占位
      return <Input size="small" disabled value="暂不支持在表格模式编辑" style={{ width: '100%' }} />;
    case 'input':
    default:
      // 组合输入安全：组合期间冻结本地值，避免上游重渲染强写 DOM 打断拼音输入
      return (
        <ComposingSafeInput
          size="small"
          value={value ?? ''}
          placeholder={elementProps.placeholder || '请输入'}
          maxLength={elementProps.maxLength}
          style={{ width: '100%' }}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

/** 属性单元格控件（memo：row 引用不变 + onUpdate 稳定即跳过） */
export const PropField = React.memo(function PropField({ id, schemaItem, row, onUpdate, structuredVersion, forceValidate }: {
  id: number; schemaItem: any; row: Record<string, any>; onUpdate: (id: number, code: string, value: any) => void; structuredVersion?: number;
  /** 外部全量校验态：true 时所有必填未填格标红（输入期只看本格是否被编辑过，避免扩散） */
  forceValidate?: boolean;
}) {
  const code = getPropertyCode(schemaItem);
  const formMap = useMemo(() => cooperationValueToFormValue(row.property_values), [row.property_values]);
  const fieldConfig = useMemo(
    () => getSchemaItemFieldConfig(schemaItem, formMap, structuredVersion),
    [schemaItem, formMap, structuredVersion],
  );
  const cur = formMap[code];
  const required = getPropRequired(schemaItem, formMap);
  /**
   * 输入期校验只作用于本格：仅当用户编辑过该格、或外部触发了全量校验时才提示。
   * 否则任一格输入都会因整表重渲染而让全表必填空格一起标红（扩散）。
   */
  const [touched, setTouched] = useState(false);
  // 选项不匹配：级联父字段（如「主平台官单/私单」）变化后，本字段旧值可能已不在当前合法选项集内。
  // ⚠️ options 取自 fieldConfig（按本行 formMap 求值），故父字段一变即重算、本格立刻标红。
  // ⚠️ input / text / numeric / date 的 elementProps **没有 options**（undefined）→
  //    isPropValueMatched 直接放行，这些控件不会误报「选项不匹配」；只有 dropdown_* 才参与该判定。
  // 与必填互斥（一条判非空、一条判空），可用同一个 errorMsg 承载。
  const mismatch = !isPropValueMatched(cur, fieldConfig.elementProps?.options);
  const missing = required && isEmptyFieldValue(cur);
  const invalid = mismatch || missing;
  const errorMsg = mismatch ? '选项不匹配' : (missing ? '此项必填' : undefined);
  /**
   * 输入期提示防抖（时长与引擎 INPUT_VALIDATE_DEBOUNCE_MS 一致）：
   * 值从「空 → 非空」时 touched 置位与受控值更新可能不在同一批次，直接判定会先按空值标红、
   * 下一帧值到位又取消，表现为红框一闪；延迟判定即可落在值更新之后。
   */
  const [debouncedInvalid, setDebouncedInvalid] = useState(false);
  useEffect(() => {
    if (forceValidate || !touched) return undefined;
    const timer = setTimeout(() => setDebouncedInvalid(invalid), INPUT_VALIDATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [invalid, touched, forceValidate]);
  const hasError = forceValidate ? invalid : debouncedInvalid;

  const control = renderPropertyControl(fieldConfig, cur, (v) => {
    setTouched(true);
    onUpdate(id, code, v);
  });

  /**
   * ⚠️ 结构必须保持稳定：始终渲染同一层 Form.Item / Tooltip，只切换 validateStatus 与 title。
   * 否则红框出现或消失会连带卸载重建 control，导致输入框失焦。
   * w-full：Form.Item 内容层是 flex 容器，包裹层需显式撑满，否则无固定宽度的子组件
   * （如 TextAreaPopover）会被 shrink 到内容宽度、铺不满 cell。
   */
  return (
    <Form.Item validateStatus={hasError ? 'error' : undefined} className="mb-0 [&_.rock-form-item-label]:hidden">
      <Tooltip title={hasError ? errorMsg : undefined} placement="top">
        <div className="rounded-[6px] w-full">{control}</div>
      </Tooltip>
    </Form.Item>
  );
});

/**
 * 「钉在左侧」列头按钮。
 *
 * 用 fixedButton 而非 titleExtra：后者随标题参与布局流、会给标题「预留」空间，
 * 标题换行或宽窄变化时挂件位置随之漂移；fixedButton 与标题布局完全解耦
 *（引擎的 ColumnFixedButtonLayer 负责把它定位到列头 padding 外沿的左上角）。
 */
function PinButton({ pinned, onToggle }: { pinned: boolean; onToggle: () => void }) {
  return (
    <span
      className={[
        'inline-flex items-center whitespace-nowrap rounded-br-md cursor-pointer',
        pinned ? 'bg-[#dfdfe5] text-[var(--text-color-2)]' : 'bg-[#e8ecff] text-[var(--primary-1)]',
      ].join(' ')}
      style={{ fontSize: 12, lineHeight: '18px', padding: '0 8px' }}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      title={pinned ? '取消钉住' : '钉在左侧'}
    >
      <Pushpin style={{ fontSize: 12, marginRight: 4 }} />
      {pinned ? '已钉住' : '钉在左侧'}
    </span>
  );
}

export interface BuildPropertyColumnsParams {
  schema: any;
  structuredVersion?: number;
  /** 已钉住（fixed:'left'）的属性列 code 集合 */
  pinnedCodes: string[];
  togglePin: (code: string) => void;
  /** 外部全量校验态：透传给 PropField */
  forceValidateAll: boolean;
  updateProperty: (id: number, code: string, v: any) => void;
  /** 查看态：不提供批量编辑入口 */
  view: boolean;
  batchApplyProperty: (code: string, bv: any, mode: 'keep' | 'cover') => void;
}

/** 构建属性列（standards + properties 依序，每属性一列） */
export function buildPropertyColumns({
  schema,
  structuredVersion,
  pinnedCodes,
  togglePin,
  forceValidateAll,
  updateProperty,
  view,
  batchApplyProperty,
}: BuildPropertyColumnsParams): ColumnMeta<any, any>[] {
  const out: ColumnMeta<any, any>[] = [];
  // 属性列：standards + properties 依序，每属性一列（custom，拿整行级联）
  const attrList = [
    ...((schema?.standards || []) as any[]),
    ...((schema?.properties || []) as any[]),
  ];
  for (const schemaItem of attrList) {
    const code = getPropertyCode(schemaItem);
    // 批量编辑用字段配置：空 formMap（无行上下文），控件类型/交互与单元格 renderPropertyControl 对齐（级联列 options 依赖行级，批量弹层暂用全配置，后续可按需补行级级联）
    const fieldConfig = getSchemaItemFieldConfig(schemaItem, {}, structuredVersion);
    out.push({
      kind: 'custom',
      custom: {
        key: code,
        title: schemaItem.label ?? schemaItem.property_name,
        width: getAttrColumnWidth(schemaItem),
        // 列宽可拖拽（#45963：全量字段展示样式（默认最小宽度），要支持用户自己拖拽列宽）
        resizable: true,
        // 钉在左侧：单选钉住属性列（fixed:'left'），列头 Pushpin 按钮切换（参考商务确认报价-中选供应商表格）
        fixed: pinnedCodes.includes(code) ? 'left' : undefined,
        fixedButton: {
          left: <PinButton pinned={pinnedCodes.includes(code)} onToggle={() => togglePin(code)} />,
        },
        render: (row: Record<string, any>) => (
          <PropField
            id={row.id}
            schemaItem={schemaItem}
            row={row}
            onUpdate={updateProperty}
            structuredVersion={structuredVersion}
            forceValidate={forceValidateAll}
          />
        ),
        // 属性列参与表格整体校验（引擎按列顺序遍历 custom 列的 validate，命中即滚动定位）
        validate: (row: Record<string, any>) => validatePropField(schemaItem, row, structuredVersion),
        // 级联枚举列（options 随行动态求值）**不提供批量编辑**：
        // 批量编辑语义是「一个值覆盖所有行」，而级联列每行的合法值域不同，
        // 同一次覆盖对多数行都是非法值（详见 isCascadingAttr）。
        ...(view || isCascadingAttr(schemaItem) ? {} : {
          batchEdit: {
            type: fieldConfig.controlType === 'select' ? 'select' : 'input',
            // 批量弹层星号与列必填同源（schema required_mode / schemaItem.required）
            required: isSchemaItemRequired(schemaItem, {}),
            render: (v: any, onC: (v: any) => void) => renderPropertyControl(fieldConfig, v, onC),
            apply: (value: any, mode: 'keep' | 'cover') => batchApplyProperty(code, value, mode),
          },
        }),
      },
    });
  }
  return out;
}
